import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useApiClients } from '../context/ApiClientsContext';
import { useUserIdentityContext } from '../context/UserIdentityContext';
import type {
  StatusValue,
  StatusFactorSummary,
  CheckInPeriod,
} from 'types/spherePoints';
import type { VelocityBucketKey } from 'types/sphereMetrics';

/**
 * Represents a single pending check-in that the current user needs to complete.
 */
export interface PendingCheckIn {
  factorId: string;
  factorType: string;
  sphereId: string;
  periodId: string;
  dueAt: string;
  isOverdue: boolean;
  currentStatus: StatusValue;
  velocityScore?: number;
  velocityBucket?: VelocityBucketKey;
  weight: number;
}

export interface UseStatusCheckInOptions {
  sphereId: string | undefined;
  /** Factor summaries from useSphereStatus — manual factors are extracted internally */
  factors: StatusFactorSummary[];
  /** Called after a successful check-in so the parent can refetch status + timeline */
  onCheckInComplete?: () => void;
}

export interface UseStatusCheckInResult {
  /** Most urgent pending check-in (overdue first, then nearest due date) */
  pendingCheckIn: PendingCheckIn | null;
  /** Total number of pending check-ins for the current user */
  pendingCount: number;
  loading: boolean;
  isSubmitting: boolean;
  error: string | null;
  submitCheckIn: (statusValue: StatusValue, comment?: string) => Promise<void>;
}

/**
 * Hook for detecting pending check-in periods and submitting check-ins.
 *
 * Filters manual factors from the provided status factors, queries the periods API
 * for each, and identifies periods where the current user is responsible and hasn't
 * checked in. Returns the most urgent pending check-in and a submission function.
 */
export function useStatusCheckIn({
  sphereId,
  factors,
  onCheckInComplete,
}: UseStatusCheckInOptions): UseStatusCheckInResult {
  const apiClients = useApiClients();
  const { identityId } = useUserIdentityContext();
  const currentUserId = identityId ?? undefined;

  const [allPendingCheckIns, setAllPendingCheckIns] = useState<
    PendingCheckIn[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchInFlightRef = useRef(false);

  const manualFactors = useMemo(
    () => factors.filter((f) => f.factorType === 'manual'),
    [factors]
  );

  // Stable serialized key to detect when manual factors actually change
  const manualFactorKey = useMemo(
    () => manualFactors.map((f) => f.id).join(','),
    [manualFactors]
  );

  // Ref to access current manual factors without adding array to callback deps
  const manualFactorsRef = useRef(manualFactors);
  manualFactorsRef.current = manualFactors;

  const fetchPendingPeriods = useCallback(async () => {
    const currentManualFactors = manualFactorsRef.current;

    if (
      !sphereId ||
      !apiClients?.graphClient ||
      !currentUserId ||
      currentManualFactors.length === 0
    ) {
      setAllPendingCheckIns([]);
      setLoading(false);
      return;
    }

    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    try {
      // Fetch periods for all manual factors in parallel
      const periodResults = await Promise.all(
        currentManualFactors.map(async (factor) => {
          try {
            const response = await apiClients.graphClient.get(
              `/factors/${factor.id}/periods?status=pending,overdue&limit=5`,
              { signal: controller.signal }
            );
            if (controller.signal.aborted) return null;
            if (response.success && Array.isArray(response.periods)) {
              return {
                factor,
                periods: response.periods as CheckInPeriod[],
              };
            }
            return null;
          } catch (err: unknown) {
            if (err instanceof DOMException && err.name === 'AbortError')
              return null;
            if (controller.signal.aborted) return null;
            console.error(
              `[useStatusCheckIn] Failed to fetch periods for factor ${factor.id}:`,
              err
            );
            return null;
          }
        })
      );

      if (controller.signal.aborted) return;

      const pendingCheckIns: PendingCheckIn[] = [];

      for (const result of periodResults) {
        if (!result) continue;
        const { factor, periods } = result;

        for (const period of periods) {
          // Check if current user is responsible and hasn't checked in or been skipped
          const userEntry = period.responsibleUsers?.find(
            (u) =>
              u.userId === currentUserId && !u.checkedIn && !u.skipped
          );
          if (!userEntry) continue;

          const isOverdue =
            period.status === 'overdue' ||
            new Date(period.dueAt) < new Date();

          pendingCheckIns.push({
            factorId: factor.id,
            factorType: factor.factorType,
            sphereId,
            periodId: period.id,
            dueAt: period.dueAt,
            isOverdue,
            currentStatus: factor.status,
            velocityScore: factor.velocityScore,
            velocityBucket: factor.velocityBucket,
            weight: factor.weight,
          });
        }
      }

      // Overdue first, then nearest due date
      pendingCheckIns.sort((a, b) => {
        if (a.isOverdue && !b.isOverdue) return -1;
        if (!a.isOverdue && b.isOverdue) return 1;
        return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
      });

      setAllPendingCheckIns(pendingCheckIns);
      setError(null);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (controller.signal.aborted) return;

      console.error(
        '[useStatusCheckIn] Failed to fetch pending periods:',
        err
      );
      setError('statusPanel.error.fetchFailed');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
      fetchInFlightRef.current = false;
    }
  }, [sphereId, apiClients?.graphClient, currentUserId, manualFactorKey]);

  // Fetch when sphere, factors, or user changes
  useEffect(() => {
    fetchPendingPeriods();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      fetchInFlightRef.current = false;
    };
  }, [fetchPendingPeriods]);

  const submitCheckIn = useCallback(
    async (statusValue: StatusValue, comment?: string) => {
      const current = allPendingCheckIns[0];
      if (!current || !apiClients?.graphClient) return;

      setIsSubmitting(true);
      setError(null);

      try {
        const body: Record<string, unknown> = { statusValue };
        if (comment) body.comment = comment;
        body.periodId = current.periodId;

        await apiClients.graphClient.post(
          `/sphere/${current.sphereId}/factors/${current.factorId}/checkin`,
          body
        );

        // Remove completed check-in and auto-advance to next
        setAllPendingCheckIns((prev) => prev.slice(1));

        onCheckInComplete?.();
      } catch (err: unknown) {
        console.error('[useStatusCheckIn] Failed to submit check-in:', err);
        setError('statusPanel.error.submitFailed');
      } finally {
        setIsSubmitting(false);
      }
    },
    [allPendingCheckIns, apiClients?.graphClient, onCheckInComplete]
  );

  return {
    pendingCheckIn: allPendingCheckIns[0] ?? null,
    pendingCount: allPendingCheckIns.length,
    loading,
    isSubmitting,
    error,
    submitCheckIn,
  };
}
